import { useState } from 'react'
import { Album } from '../../../../types'
import { moveAlbum } from '../lib/folder-manager'
import { folderLogger } from '../lib/logger'
import type { Feedback } from './use-feedback'

export interface AlbumDragDrop {
  draggedAlbum: Album | null
  isDragging: boolean
  handleAlbumDragStart: (album: Album) => void
  handleAlbumDragEnd: () => void
  handleAlbumDrop: (album: Album | null, targetAlbumId: string | undefined) => Promise<void>
  clearDragState: () => void
}

// Album drag/drop, moved verbatim out of album-list.tsx (SOL-150 Task 6).
export function useAlbumDragDrop(args: {
  albums: Album[]
  updateAlbums: (albums: Album[]) => void
  feedback: Feedback
}): AlbumDragDrop {
  const { albums, updateAlbums, feedback } = args
  const { showNotification } = feedback

  const [draggedAlbum, setDraggedAlbum] = useState<Album | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  // Shared by handleNavigateToFolder and handleAlbumDoubleClick in
  // album-list.tsx, which both stay there because they own currentFolderId
  // and clear drag state immediately when navigating/entering an album.
  const clearDragState = () => {
    setDraggedAlbum(null)
    setIsDragging(false)
  }

  const handleAlbumDragStart = (album: Album) => {
    setDraggedAlbum(album)
    setIsDragging(true)
    folderLogger.debug('Album drag started', {
      albumId: album.uuid,
      albumName: album.name
    })
  }

  const handleAlbumDragEnd = () => {
    setDraggedAlbum(null)
    setIsDragging(false)
    folderLogger.debug('Album drag ended')
  }

  const handleAlbumDrop = async (album: Album | null, targetAlbumId: string | undefined) => {
    const albumToDrop = album || draggedAlbum

    if (!albumToDrop) {
      folderLogger.warn('No album data available for drop')
      return
    }

    folderLogger.debug('Processing album drop', {
      sourceAlbumId: albumToDrop.uuid,
      targetAlbumId,
      source: album ? 'dataTransfer' : 'internalState'
    })

    const result = moveAlbum(albums, albumToDrop.uuid, targetAlbumId)

    if (result.success && result.albums) {
      updateAlbums(result.albums)
      folderLogger.info('Album moved successfully', {
        sourceAlbumId: albumToDrop.uuid,
        targetAlbumId,
        albumName: albumToDrop.name
      })
    } else {
      showNotification('error', 'Error', result.error || 'Failed to move album')
    }
  }

  return {
    draggedAlbum,
    isDragging,
    handleAlbumDragStart,
    handleAlbumDragEnd,
    handleAlbumDrop,
    clearDragState,
  }
}
